// src/services/medicineTransactionService.js
import apiClient from "./apiClient";

const API_URL = `/medicine-transactions`; // Use relative path since apiClient has baseURL

const toPositiveId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object" && !Array.isArray(value)) {
    return toPositiveId(
      value.id ??
        value.prescriptionId ??
        value.prescription_id ??
        value.invoiceId ??
        value.invoice_id
    );
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
};

/**
 * Ensure invoice/prescription FKs are sent as both camelCase and snake_case
 * so org medicine_transactions inserts succeed for either DTO naming style.
 */
export const normalizeMedicineTransactionRelationIds = (payload = {}) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return payload;
  }

  const next = { ...payload };

  const invoiceId = toPositiveId(
    next.invoiceId ?? next.invoice_id ?? next.invoice
  );
  if (invoiceId != null) {
    next.invoiceId = invoiceId;
    next.invoice_id = invoiceId;
  }

  const prescriptionId = toPositiveId(
    next.prescriptionId ??
      next.prescription_id ??
      next.prescription ??
      next.ipdPrescriptionId ??
      next.ipd_prescription_id
  );
  if (prescriptionId != null) {
    next.prescriptionId = prescriptionId;
    next.prescription_id = prescriptionId;
  }

  const patientId = toPositiveId(next.patientId ?? next.patient_id ?? next.patient);
  if (patientId != null) {
    next.patientId = patientId;
    next.patient_id = patientId;
  }

  const admissionId = toPositiveId(
    next.admissionId ?? next.admission_id ?? next.admission
  );
  if (admissionId != null) {
    next.admissionId = admissionId;
    next.admission_id = admissionId;
  }

  return next;
};

// Create a new medicine transaction record
export const createMedicineTransaction = async (transactionData) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const payload = normalizeMedicineTransactionRelationIds(transactionData);
    const response = await apiClient.post(API_URL, payload);
    return response.data;
  } catch (error) {
    console.error("Error creating medicine transaction:", error);
    throw error;
  }
};

// Get all medicine transactions
export const getAllMedicineTransactions = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(API_URL);
    return response.data;
  } catch (error) {
    console.error("Error fetching medicine transactions:", error);
    throw error;
  }
};

// Get medicine transactions by patient ID
export const getMedicineTransactionsByPatientId = async (patientId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/patient/${patientId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching medicine transactions by patient:", error);
    throw error;
  }
};

// Get medicine transactions by medicine ID
export const getMedicineTransactionsByMedicineId = async (medicineId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/medicine/${medicineId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching medicine transactions by medicine:", error);
    throw error;
  }
};

// Get medicine transactions by batch number
export const getMedicineTransactionsByBatchNumber = async (batchNumber) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/batch/${batchNumber}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching medicine transactions by batch:", error);
    throw error;
  }
};

// Get medicine transactions by patient and medicine for IPD treatments
export const getMedicineTransactionsByPatientAndMedicine = async (patientId, medicineId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/patient/${patientId}/medicine/${medicineId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching medicine transactions by patient and medicine:", error);
    throw error;
  }
};

// Deduct medicine inventory with transaction tracking
export const deductMedicineInventoryWithHistory = async (deductionData) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const payload = normalizeMedicineTransactionRelationIds(deductionData);
    const response = await apiClient.post(`${API_URL}/deduct-with-history`, payload);
    return response.data;
  } catch (error) {
    console.error("Error deducting medicine inventory with history:", error);
    throw error;
  }
};
